import { HandleType } from '@/enums/handle-type.enum';
import { useWorkflowStore } from '@/state/workflow.state';
import { track } from '@/utils/analytics';
import { FIGMA_NODE_EVENTS } from '@/components/Nodes/FigmaNode/figmaNode.analytics';
import { UndoRedoEntry } from '../undoRedo';
import type { FigmaNodeData, Handle } from '@/types/node';
import type { FlowGraph } from '../FlowGraph';
import type { ConnectedFigmaFrame, FigmaNodeSnapshot, ImageFile, NodeId, PendingFigmaNodeSnapshotCopy } from 'web';

export type FigmaNodeHandleType = HandleType.Image | HandleType.Text;
type FigmaNodeVariablePrefix = 'image' | 'text';

function variablePrefix(handleType: FigmaNodeHandleType): FigmaNodeVariablePrefix {
  switch (handleType) {
    case HandleType.Image:
      return 'image';
    case HandleType.Text:
      return 'text';
  }
}

function variableLabel(handleType: FigmaNodeHandleType, index: number): string {
  const label = handleType === HandleType.Image ? 'Image' : 'Text';
  return index === 1 ? label : `${label} ${index}`;
}

export class FigmaNodeView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  /**
   * Persists a post-initialize snapshot to both the TS store and the wasm
   * graph. No-ops if the node is gone. Wasm clears `pendingSnapshotCopy` in the same edit.
   */
  applySnapshot(snapshot: FigmaNodeSnapshot) {
    this.writeSnapshot(snapshot, { pastedData: undefined });
  }

  /**
   * Installs a copied snapshot only while the node still owns the pending source request that
   * produced it. Returns false when an undo/removal or newer request made the response stale.
   * Clears pending in both TS and wasm (via `applyFigmaNodeInitialize`).
   */
  applyCopiedSnapshot(pendingSnapshotCopy: PendingFigmaNodeSnapshotCopy, snapshot: FigmaNodeSnapshot): boolean {
    if (!this.hasPendingSnapshotCopy(pendingSnapshotCopy)) return false;

    this.writeSnapshot(snapshot, {
      connectedFigmaFrame: undefined,
      pastedData: undefined,
      pendingSnapshotCopy: undefined,
    });
    return true;
  }

  /** Returns whether this node still has the exact pending snapshot-copy request. */
  hasPendingSnapshotCopy(pendingSnapshotCopy: PendingFigmaNodeSnapshotCopy): boolean {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return false;
    const current = this.getFigmaNodeData()?.pendingSnapshotCopy;
    if (!current) return false;
    return (
      current.sourceSnapshot.snapshotKey === pendingSnapshotCopy.sourceSnapshot.snapshotKey &&
      current.sourceSnapshot.snapshotVersionId === pendingSnapshotCopy.sourceSnapshot.snapshotVersionId
    );
  }

  private writeSnapshot(snapshot: FigmaNodeSnapshot, patch: Partial<FigmaNodeData>) {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return;

    const outputImage: ImageFile = {
      url: snapshot.outputAsset.url,
      width: snapshot.outputAsset.width,
      height: snapshot.outputAsset.height,
      thumbnailUrl: undefined,
      publicId: undefined,
      id: undefined,
    };

    const undoEntry = this.graph.wasmChange((wasm) =>
      wasm.applyFigmaNodeInitialize(this.nodeId, snapshot, outputImage),
    );
    undoEntry.add(
      this.graph.store.updateNodeData<FigmaNodeData>(this.nodeId, () => ({
        snapshot,
        outputImage,
        ...patch,
      })),
    );
    this.graph.updateNodeOutputs(this.nodeId);
    this.graph.loadResources();
  }

  applyConnectedFigmaFrame(frame: ConnectedFigmaFrame) {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return;

    const undoEntry = this.graph.wasmChange((wasm) => wasm.setFigmaNodeConnectedFigmaFrame(this.nodeId, frame));
    undoEntry.add(this.graph.store.updateNodeData<FigmaNodeData>(this.nodeId, () => ({ connectedFigmaFrame: frame })));
  }

  /**
   * Sets `lastEditedAt` to the observed Figma value (and `lastSyncedAt` too when `wasSynced`).
   * No-op if disconnected or if nothing changes.
   */
  applyConnectedFrameSync(sync: { lastEditedAt: number; wasSynced: boolean }) {
    const frame = this.getFigmaNodeData()?.connectedFigmaFrame;
    if (!frame) return;
    const lastEditedAt = sync.lastEditedAt;
    const lastSyncedAt = sync.wasSynced ? sync.lastEditedAt : frame.lastSyncedAt;
    if (lastEditedAt === frame.lastEditedAt && lastSyncedAt === frame.lastSyncedAt) return;
    this.applyConnectedFigmaFrame({ ...frame, lastEditedAt, lastSyncedAt });
  }

  getConnectedFigmaFrame(): ConnectedFigmaFrame | undefined {
    return this.getFigmaNodeData()?.connectedFigmaFrame;
  }

  clearConnectedFigmaFrame() {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return;

    const undoEntry = this.graph.wasmChange((wasm) => wasm.setFigmaNodeConnectedFigmaFrame(this.nodeId, null));
    undoEntry.add(
      this.graph.store.updateNodeData<FigmaNodeData>(this.nodeId, () => ({ connectedFigmaFrame: undefined })),
    );
  }

  addVariableInput(handleType: FigmaNodeHandleType) {
    this.graph.edit(() => this.addVariableInputEntry(handleType).undoEntry);
  }

  addVariableInputEntry(handleType: FigmaNodeHandleType): { key: string; undoEntry: UndoRedoEntry } {
    const { key, index } = this.nextVariableKey(handleType);
    const weaveVariableType = variablePrefix(handleType);
    // Pass the type as the handle's valid input type so the wasm node records `weaveVariableType`.
    const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, key, [weaveVariableType]));

    undoEntry.add(
      this.graph.store.updateNodeData<FigmaNodeData>(this.nodeId, (nodeData) => {
        const inputHandles = Array.isArray(nodeData.handles.input) ? {} : nodeData.handles.input;
        const handle: Handle = {
          description: 'Figma variable',
          format: handleType === HandleType.Text ? 'text' : 'uri',
          id: key,
          order: Object.keys(inputHandles).length,
          required: false,
          label: variableLabel(handleType, index),
          type: handleType,
        };

        return {
          inputs: [
            ...(nodeData.inputs ?? []).filter((input) => input.inputId !== key),
            {
              inputId: key,
              weaveVariableId: key,
              weaveVariableType,
              input: undefined,
            },
          ],
          handles: {
            ...nodeData.handles,
            input: {
              ...inputHandles,
              [key]: handle,
            },
          },
        };
      }),
    );

    track(FIGMA_NODE_EVENTS.inputAdded, {
      workflowId: useWorkflowStore.getState().recipe.id,
      nodeId: this.nodeId,
      inputType: handleType,
    });

    return { key, undoEntry };
  }

  removeVariableInput(key: string) {
    this.graph.edit(() => this.removeVariableInputEntry(key));
  }

  /**
   * Removes an input handle and its binding together (the binding carries the value, so they must
   * stay in lockstep), plus any edge feeding the handle. Mirrors {@link addVariableInputEntry}.
   */
  removeVariableInputEntry(key: string): UndoRedoEntry {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return UndoRedoEntry.empty();

    // Prune feeding edges before removing the handle: edge removal calls `setNodeInput`, which
    // throws if the input handle is already gone.
    // Matches `getHandleId(nodeId, 'input', key)`; exact-match avoids substring collisions (e.g. `text1` vs `text12`).
    const targetHandleId = `${this.nodeId}-input-${key}`;
    const edgeIds = this.graph.store
      .getEdges()
      .filter((edge) => edge.target === this.nodeId && edge.targetHandle === targetHandleId)
      .map((edge) => edge.id);
    const undoEntry = edgeIds.length > 0 ? this.graph.removeEdges(edgeIds) : UndoRedoEntry.empty();

    undoEntry.add(this.graph.wasmChange((wasm) => wasm.removeInputHandle(this.nodeId, key)));

    undoEntry.add(
      this.graph.store.updateNodeData<FigmaNodeData>(this.nodeId, (nodeData) => {
        const inputHandles = Array.isArray(nodeData.handles.input) ? {} : { ...nodeData.handles.input };
        delete inputHandles[key];
        return {
          inputs: (nodeData.inputs ?? []).filter((input) => input.inputId !== key && input.weaveVariableId !== key),
          handles: { ...nodeData.handles, input: inputHandles },
        };
      }),
    );

    return undoEntry;
  }

  private getFigmaNodeData(): FigmaNodeData | undefined {
    return this.graph.store.getNodeDataNullable<FigmaNodeData>(this.nodeId);
  }

  private nextVariableKey(handleType: FigmaNodeHandleType): { key: string; index: number } {
    const data = this.getFigmaNodeData();
    const prefix = variablePrefix(handleType);
    const usedKeys = new Set([
      ...(data?.inputs ?? []).flatMap((input) => [input.inputId, input.weaveVariableId]),
      ...Object.keys(Array.isArray(data?.handles.input) ? {} : (data?.handles.input ?? {})),
    ]);

    let index = 1;
    while (usedKeys.has(`${prefix}${index}`)) {
      index += 1;
    }

    return { key: `${prefix}${index}`, index };
  }
}
